import { Input } from "./ui/input";
import { Chip } from "./lux";

export const PERIODS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "all", label: "All time" },
  { key: "custom", label: "Custom range" },
];

const iso = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Local-time ranges — a booking that ended "today" must not shift a day.
export function periodRange(key, custom = {}) {
  const now = new Date();
  if (key === "today") return { from: iso(now), to: iso(now), label: "today" };
  if (key === "week") {
    const start = new Date(now);
    start.setDate(now.getDate() - ((now.getDay() + 6) % 7)); // Monday
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { from: iso(start), to: iso(end), label: "this week" };
  }
  if (key === "last_month") {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: iso(start), to: iso(end), label: "last month" };
  }
  if (key === "custom") {
    return { from: custom.from || "", to: custom.to || "", label: "this range" };
  }
  // SALES-002: unbounded. Empty strings are already how every caller signals it
  // — they send `range.from || undefined`, so the dates leave the query string
  // entirely. Must sit ABOVE the fallback below, which answers for "month" AND
  // for any unknown key, and would quietly turn all time into this month.
  if (key === "all") return { from: "", to: "", label: "all time" };
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: iso(start), to: iso(end), label: "this month" };
}

export function PeriodPicker({ value, onChange, custom, onCustom, testid = "period-picker" }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      {PERIODS.map((p) => (
        <Chip key={p.key} active={value === p.key} onClick={() => onChange(p.key)} testid={`${testid}-${p.key}`}>
          {p.label}
        </Chip>
      ))}
      {value === "custom" && (
        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 w-full sm:flex sm:w-auto">
          <Input type="date" value={custom?.from || ""} onChange={(e) => onCustom({ ...custom, from: e.target.value })} className="bg-input border-border h-9 min-w-0 pl-3 sm:pl-4 sm:w-44" data-testid={`${testid}-from`} />
          <span className="text-muted-foreground text-xs">to</span>
          <Input type="date" value={custom?.to || ""} onChange={(e) => onCustom({ ...custom, to: e.target.value })} className="bg-input border-border h-9 min-w-0 pl-3 sm:pl-4 sm:w-44" data-testid={`${testid}-to`} />
        </div>
      )}
    </div>
  );
}

// "1h 42m" from minutes; "—" when there is nothing to measure.
export const humanMinutes = (mins) => {
  if (mins === null || mins === undefined) return "—";
  const m = Math.round(Number(mins));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
};
